import React, { memo, useState } from 'react';
import ShowHitDialog from './ShowHitDialog';

const getGradientColor = (value) => {
  if (value<0 || value>1) {
    return 'red';
  }
  const lightness = 70 - 40 * value;
  return `hsl(210, 80%, ${lightness}%)`;
};

const FeaturesTable = memo(({ data, visibleColumns, visibleScores, availableRanking, sortBy, sortOrder, onSortChange }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!data || data.length === 0) {
    return null
  }

  // When ranking is available, _ranking_score is the first column; then scores, then columns
  const allColumns = [
    ...(availableRanking ? ['_ranking_score'] : []),
    ...(visibleScores || []),
    ...(visibleColumns || []),
  ];
  const isScoreColumn = (col) => visibleScores?.includes(col);
  const isRankingColumn = (col) => col === '_ranking_score';

  const handleRowClick = (item) => {
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const handleHeaderClick = (col) => {
    if (sortBy === col) {
      // If clicking the same column, reverse direction
      onSortChange(col, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new column, set it as sort column and default to descending
      onSortChange(col, 'desc');
    }
  };

  return (
    <>
      <style>{`
        tr[data-hoverable]:hover {
          box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.5);
        }
        tr[data-hoverable]:hover td {
          filter: brightness(0.85);
        }
      `}</style>
      <table style={{ 
      borderCollapse: 'collapse',
      marginTop: '16px'
    }}>
      <thead>
        <tr style={{ backgroundColor: '#f5f5f5', height: '300px' }}>
          {allColumns.map((col) => {
            const isScore = isScoreColumn(col);
            const isRanking = isRankingColumn(col);
            const defaultBgColor = isRanking ? '#ffcc80' : isScore ? '#fffacd' : '#f5f5f5'; // Orange for ranking, light yellow for scores
            const isSorted = sortBy === col;
            return (
              <th
                key={col}
                onClick={() => handleHeaderClick(col)}
                style={{
                  width: '25px',
                  padding: '0',
                  fontWeight: 600,
                  borderRight: '1px solid #eee',
                  borderBottom: '2px solid #ddd',
                  cursor: 'pointer',
                  backgroundColor: defaultBgColor,
                }}
              >
                <div style={{
                  width: '25px',
                  height: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{
                    transform: 'rotate(-90deg)',
                    transformOrigin: 'center',
                    whiteSpace: 'nowrap',
                    fontSize: '13px',
                    fontWeight: isSorted ? 700 : 600,
                    fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace",
                  }}>
                    {col}
                    {isSorted && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortOrder === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </span>
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {data.map((item, idx) => (
          <tr
            key={idx}
            data-hoverable
            onClick={() => handleRowClick(item)}
            style={{
              cursor: 'pointer',
            }}
          >
            {allColumns.map((col) => {
              const isScore = isScoreColumn(col);
              const isRanking = isRankingColumn(col);
              const value = isRanking ? item._ranking_score : isScore ? item._scores?.[col] : item._features?.[col];
              const isNumeric = typeof value === 'number';
              const backgroundColor = isNumeric ? getGradientColor(value) : undefined;
              return (
                <td
                  key={`${idx}-${col}`}
                  style={{
                    width: '25px',
                    height: '15px',
                    padding: '0',
                    borderRight: '1px solid #eee',
                    borderBottom: '1px solid #eee',
                    backgroundColor: backgroundColor,
                  }}
                />
              );
            })}
          </tr>
        ))}
      </tbody>
      </table>

      <ShowHitDialog
        open={dialogOpen}
        hit={selectedItem}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
});

export default FeaturesTable;

